// usePlannerCompare.ts
import React, { createContext, useContext, useMemo, useState } from "react";
import { useReferencePlan, ReferencePlanState, useWeeklyPlan, usePreTeachData } from "@/hooks";
import { AssignmentCategory, AssignmentStatus } from "@/enums";
import { PlanningSummaryProps } from "@/features/dashboard/components";
import { INCOMPLETE_STATUSES } from "@/components/Reports/util/getUsageBreakdown";

export interface PlannerCompare extends PlanningSummaryProps {
  isComparable: boolean;
  hasRollover: boolean;
}

const defaultValue: PlannerCompare = {
  isComparable: false,
  hasRollover: true,
  incompleteAssignments: 0,
  studentsStruggling: 0,
  studentsShowedGrowth: 0,
  studentsNotReady: 0,
  skillsToBeTaught: 0,
  studentsUnderbooked: 0,
};

const PlannerCompareContext = createContext<PlannerCompare | null>(null);

export const usePlannerCompare = (): PlannerCompare => {
  const context = useContext(PlannerCompareContext);
  if (context) return context; // Early eject for testing

  // Get the current and reference (last week) plan data
  const { data: currentPlanData } = useWeeklyPlan();
  const { data: referencePlanData } = useReferencePlan() as ReferencePlanState;
  const { 
    isLoading: isRolloverLoading,
    nextWeekFocusSkills,
    redKids
  } = usePreTeachData();
  const {
    isComparable,
    hasRollover,
  } = useMemo(() => {
    return {
      // Check if the current plan and reference plan are comparable
      isComparable: !!currentPlanData 
        && currentPlanData.assignments
        && currentPlanData.assignments.length > 0 
        && !!referencePlanData
        && referencePlanData.assignments
        && referencePlanData.assignments.length > 0,
     // Check if there are rollover or prerequisite assignments in the current plan
     hasRollover: currentPlanData
        ?.assignments
        ?.some(({
          assignmentCategory: assignmentCategories
        }) => assignmentCategories?.some(assignmentCategory => [AssignmentCategory.ROLLOVER].includes(assignmentCategory))
      ) ?? false,
    }
  }, [ currentPlanData?.assignments, referencePlanData.assignments ]);

  const { 
    incompleteAssignments,
    studentsStruggling,
    studentsShowedGrowth,
  } = useMemo(() => {
    if (!isComparable || hasRollover || !referencePlanData.assignments) return {
      incompleteAssignments: 0,
      studentsStruggling: 0,
      studentsShowedGrowth: 0,
    }
    return {
      incompleteAssignments: referencePlanData.assignments.reduce((acc, { status }) => acc += ([AssignmentStatus.IN_PROGRESS, AssignmentStatus.ASSIGNED].includes(status) ? 1 : 0), 0) ?? 0,
      studentsStruggling: 99,
      studentsShowedGrowth: 99,
    };
  }, [isComparable, hasRollover, referencePlanData.assignments]);

  const {
    skillsToBeTaught,
    studentsNotReady,
    studentsUnderbooked,
  } = useMemo(() => {
    if (!isRolloverLoading || !redKids || !nextWeekFocusSkills ) return {
      skillsToBeTaught: 0,
      studentsNotReady: 0,
      studentsUnderbooked: 0,
    }
    return {
      skillsToBeTaught: nextWeekFocusSkills.length ?? 0,
      studentsNotReady: redKids.length ?? 0,
      studentsUnderbooked: 0,
    }
  }, [ isRolloverLoading, currentPlanData?.mastery, redKids, nextWeekFocusSkills ]);

  return {
    ...defaultValue,
    isComparable,
    hasRollover,
    incompleteAssignments,
    studentsStruggling,
    studentsShowedGrowth,
    studentsNotReady,
    skillsToBeTaught,
    studentsUnderbooked
  };
};

export const PlannerCompareProvider = ({
  value,
  children,
}: {
  value: PlannerCompare;
  children: React.ReactNode;
}) => (
  <PlannerCompareContext.Provider value={value}>
    {children}
  </PlannerCompareContext.Provider>
);
